import { useState, useEffect, useRef } from 'react';
import aiService, { type AIRequestOptions } from '../../../services/aiService';
import { buildGeminiFallbackChain } from '../../../services/geminiFallback';
import { Interpretation } from '../types';
import { supportsThinkingMode } from '../utils/helpers';
import { INTERPRETATION_GENERATION_PARAMS, RECOMMENDED_THINKING_BUDGET } from '../utils/interpretationPrompt';
import { useAIProvider } from './useAIProvider';

// sessionStorage key with the partial raw stream text (crash/reload recovery).
// Written (throttled, ~2 s) while a streaming generation is in progress and
// removed after a successful generation.
export const PARTIAL_STREAM_STORAGE_KEY = 'wiscv-partial-stream';
const PARTIAL_STREAM_SAVE_INTERVAL_MS = 2000;

export const useAIIntegration = (
  setStreamingText: (text: string) => void,
  setRawStreamText: (text: string) => void,
  setIsStreaming: (streaming: boolean) => void,
  rawStreamTextRef: React.MutableRefObject<string>
) => {
  // Provider/model state lives in its own hook (wave 2.4a); we consume it
  // internally and re-expose its values so the shape returned to
  // SimpleWISCVSystem stays unchanged.
  const provider = useAIProvider();
  const { selectedModel, selectedProvider, fallbackModel, geminiModels } = provider;

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [enableThinking, setEnableThinking] = useState(true);
  // 2048 = evaluated budget (2026-09-30); -1 ("Automatyczny") maps to 4096 in the backend.
  const [thinkingBudget, setThinkingBudget] = useState(RECOMMENDED_THINKING_BUDGET);
  const [thinkingProcess, setThinkingProcess] = useState('');
  const [interpretation, setInterpretation] = useState<Interpretation | null>(null);
  const [showRawResponse, setShowRawResponse] = useState(false);
  const [savedRawText, setSavedRawText] = useState('');

  // Wynik ostatniej generacji ('success' | 'error' | null podczas trwania).
  // UI zwija kartę „Konfiguracja Silnika AI" tylko po sukcesie — po błędzie
  // musi zostać rozwinięta (tam jest przycisk „Ponów").
  const [lastGenerationStatus, setLastGenerationStatus] = useState<'success' | 'error' | null>(null);

  // Model that actually answered the last generation + a user-facing notice when it
  // is a fallback model (Gemini chain: selected → 2.5 Flash → 3.5 Flash-Lite).
  const [answeredModel, setAnsweredModel] = useState<string | null>(null);
  const [generationNotice, setGenerationNotice] = useState<string | null>(null);

  // PIERWSZY TOKEN (plan 5.2, W2): choreografia UI (zwijanie kart wejściowych,
  // auto-otwarcie arkusza, puls) odpala się dopiero na krawędzi false→true tego
  // sygnału — NIE na krawędzi `loading` (W1 „Łączenie" nie zmienia layoutu;
  // błąd przed pierwszym tokenem = W5 = zero zmian wizualnych poza CTA+Dziennik).
  // Ref strzeże jednokrotności w obrębie jednej generacji; reset na starcie
  // runGeneration. Dla generacji nie-streamingowej sygnał = otrzymanie wyniku.
  const [firstChunkReceived, setFirstChunkReceived] = useState(false);
  const firstChunkSeenRef = useRef(false);

  const markFirstChunk = () => {
    if (firstChunkSeenRef.current) return;
    firstChunkSeenRef.current = true;
    setFirstChunkReceived(true);
  };

  // Partial text recovered from sessionStorage after an unexpected reload.
  // UI wiring happens in wave 2c — for now it is only exposed from the hook.
  const [recoveredPartialText, setRecoveredPartialText] = useState<string | null>(null);

  // Retry support (wave 2.5a)
  const lastGenerationArgsRef = useRef<{ prompt: string; streaming: boolean; systemPrompt?: string } | null>(null);
  const isRetryingRef = useRef(false);

  // Throttled partial-stream persistence (wave 2.5b)
  const lastPartialSaveTsRef = useRef(0);

  const addDebugLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString('pl-PL');
    setDebugLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Recover a partial stream left over from a reload during generation
  const recoveryCheckRan = useRef(false);
  useEffect(() => {
    if (recoveryCheckRan.current) return;
    recoveryCheckRan.current = true;
    try {
      const savedPartial = sessionStorage.getItem(PARTIAL_STREAM_STORAGE_KEY);
      if (savedPartial && !interpretation) {
        setRecoveredPartialText(savedPartial);
      }
    } catch {
      // sessionStorage unavailable (privacy mode) — recovery is best-effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePartialStream = (text: string, force = false) => {
    const now = Date.now();
    if (!force && now - lastPartialSaveTsRef.current < PARTIAL_STREAM_SAVE_INTERVAL_MS) return;
    lastPartialSaveTsRef.current = now;
    try {
      sessionStorage.setItem(PARTIAL_STREAM_STORAGE_KEY, text);
    } catch {
      // Quota / privacy mode — persistence is best-effort only
    }
  };

  const clearPartialStream = () => {
    try {
      sessionStorage.removeItem(PARTIAL_STREAM_STORAGE_KEY);
    } catch {
      // ignore
    }
  };

  /**
   * One generation request. Gemini goes through the fallback chain
   * (aiService.generateWithGeminiFallback); OpenRouter keeps its 429 → Gemini switch.
   * Both send the v2.1 generation params and the system prompt (when the selected
   * prompt has one) — previously the legacy UI sent neither, so the backend used
   * temperature 0.7 / 4000 tokens.
   */
  const requestGeneration = async (
    prompt: string,
    systemPrompt: string | undefined,
    onChunk: ((chunk: string) => void) | undefined,
    onStatus: (status: string) => void
  ) => {
    const requestOptions: AIRequestOptions = {
      systemPrompt,
      generationParams: { ...INTERPRETATION_GENERATION_PARAMS },
    };
    if (selectedProvider === 'gemini') {
      const chain = buildGeminiFallbackChain(selectedModel, geminiModels.map((m) => m.id));
      addDebugLog(`🔄 Kolejność modeli: ${chain.join(' → ')}`);
      const result = await aiService.generateWithGeminiFallback(prompt, chain, {
        ...requestOptions,
        thinking: enableThinking ? { enabled: true, budget: thinkingBudget } : undefined,
        onChunk,
        onStatus,
      });
      return { response: result.response, thinking: result.thinking, model: result.model };
    }
    addDebugLog(`🔄 Fallback model: ${fallbackModel}`);
    const thinkingConfig = supportsThinkingMode(selectedModel) && enableThinking
      ? { enabled: true, budget: thinkingBudget }
      : undefined;
    const result = await aiService.generateWithProvider(
      prompt,
      selectedProvider,
      selectedModel,
      thinkingConfig,
      onChunk,
      onStatus,
      fallbackModel,
      requestOptions
    );
    // After an OpenRouter 429 the Gemini chain answers — result.model names it.
    return { ...result, model: result.model ?? selectedModel };
  };

  /** Records which model answered; a notice only when it differs from the selected one. */
  const recordAnsweredModel = (model: string) => {
    setAnsweredModel(model);
    if (model !== selectedModel) {
      const notice = `Wybrany model ${selectedModel} był niedostępny — interpretację wygenerował model zapasowy ${model}.`;
      setGenerationNotice(notice);
      addDebugLog(`⚠️ ${notice}`);
    } else {
      addDebugLog(`✅ Odpowiedział model: ${model}`);
    }
  };

  const runGeneration = async (previewPrompt: string, enableStreaming: boolean, systemPrompt?: string) => {
    setLoading(true);
    setLastGenerationStatus(null);
    setAnsweredModel(null);
    setGenerationNotice(null);
    // Reset sygnału pierwszego tokena — każda generacja ma własną krawędź W2.
    firstChunkSeenRef.current = false;
    setFirstChunkReceived(false);
    setLoadingMessage('Łączenie z AI...');
    setStreamingText('');
    setRawStreamText('');
    rawStreamTextRef.current = '';
    lastPartialSaveTsRef.current = 0;
    setShowRawResponse(false);
    setThinkingProcess('');
    setDebugLogs([]);

    try {
      if (!selectedModel || !selectedProvider) {
        alert('Wybierz dostawcę AI i model przed generowaniem interpretacji');
        return;
      }

      if (enableStreaming) {
        setIsStreaming(true);
        setLoadingMessage('🌊 Przygotowuję streaming...');
        setStreamingText('');
        addDebugLog(`🚀 Rozpoczynam generowanie z ${selectedProvider} (${selectedModel})`);

        const result = await requestGeneration(
          previewPrompt,
          systemPrompt,
          (chunk: string) => {
            // W2: pierwszy NIEPUSTY chunk uruchamia choreografię UI.
            if (chunk.trim() !== '') markFirstChunk();
            rawStreamTextRef.current += chunk;
            setRawStreamText(rawStreamTextRef.current);
            savePartialStream(rawStreamTextRef.current);
          },
          (status: string) => {
            setLoadingMessage(status);
            addDebugLog(status);
          }
        );

        // Domknięcie W2 dla streamów bez niepustych chunków (wynik = treść jest).
        markFirstChunk();
        setSavedRawText(rawStreamTextRef.current);
        setInterpretation(result.response);
        recordAnsweredModel(result.model);
        if (result.thinking) {
          setThinkingProcess(result.thinking);
          addDebugLog('💭 Thinking process otrzymany');
        }

        if (!result.response.general_assessment && rawStreamTextRef.current) {
          setShowRawResponse(true);
          addDebugLog('⚠️ Parsowanie nie wypełniło rubryk - pokazuję pełną odpowiedź');
        }
        addDebugLog('✅ Interpretacja wygenerowana pomyślnie');
        clearPartialStream();
        setRecoveredPartialText(null);
        setIsStreaming(false);
      } else {
        setLoadingMessage('Generowanie interpretacji...');
        addDebugLog(`🚀 Rozpoczynam generowanie z ${selectedProvider} (${selectedModel})`);

        const result = await requestGeneration(
          previewPrompt,
          systemPrompt,
          undefined,
          (status: string) => {
            setLoadingMessage(status);
            addDebugLog(status);
          }
        );

        // Generacja nie-streamingowa: W2 = otrzymanie wyniku (plan 5.2).
        markFirstChunk();
        setInterpretation(result.response);
        recordAnsweredModel(result.model);
        if (result.thinking) {
          setThinkingProcess(result.thinking);
          addDebugLog('💭 Thinking process otrzymany');
        }
        addDebugLog('✅ Interpretacja wygenerowana pomyślnie');
        clearPartialStream();
        setRecoveredPartialText(null);
      }

      setLoadingMessage('');
      setLastGenerationStatus('success');
    } catch (error: any) {
      console.error('Błąd podczas generowania interpretacji:', error);
      addDebugLog(`❌ BŁĄD: ${error.message}`);
      setLastGenerationStatus('error');

      const currentRawText = rawStreamTextRef.current;
      if (currentRawText) {
        // The throttled saver may be up to 2s stale — persist the full tail
        // so post-reload recovery has everything that actually arrived.
        savePartialStream(currentRawText, true);
        setSavedRawText(currentRawText);
        setShowRawResponse(true);
        try {
          const partialResult = aiService.parseResponsePublic(currentRawText);
          setInterpretation(partialResult);
          addDebugLog('⚠️ Częściowa odpowiedź sparsowana z przerwanych danych');
        } catch {
          setInterpretation({
            general_assessment: currentRawText,
            strengths: '',
            areas_for_support: '',
            classroom_implications: '',
            teacher_recommendations: [],
            parent_recommendations: []
          });
        }
      } else {
        alert(`Wystąpił błąd podczas generowania interpretacji: ${error.message}`);
      }
      setIsStreaming(false);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Czyści artefakty poprzedniej generacji (surowy markdown, thinking,
   * widok raw) — używane przy wczytaniu zapisanej diagnozy, żeby panel nie
   * pokazywał markdownu/rozumowania z innej sesji. NIE rusza interpretacji.
   */
  const resetGenerationArtifacts = () => {
    setAnsweredModel(null);
    setGenerationNotice(null);
    setSavedRawText('');
    setThinkingProcess('');
    setShowRawResponse(false);
  };

  const generateInterpretation = async (previewPrompt: string, enableStreaming: boolean, systemPrompt?: string) => {
    // A fresh generation remembers its arguments (incl. the system prompt)
    // so retryGeneration() can replay the exact same request.
    lastGenerationArgsRef.current = { prompt: previewPrompt, streaming: enableStreaming, systemPrompt };
    await runGeneration(previewPrompt, enableStreaming, systemPrompt);
  };

  /**
   * Replays the last generateInterpretation call (wave 2.5a).
   * User-initiated, so any number of retries is fine — only parallel
   * invocations are guarded. (The original once-per-generation budget made
   * the button silently dead after the first failed retry: QA DEV 2026-06-10.)
   */
  const retryGeneration = async () => {
    const args = lastGenerationArgsRef.current;
    if (!args) {
      addDebugLog('⚠️ Brak poprzedniego wywołania - nie można ponowić generowania');
      return;
    }
    if (isRetryingRef.current || loading) return;
    isRetryingRef.current = true;
    addDebugLog('🔁 Ponawiam generowanie interpretacji...');
    try {
      await runGeneration(args.prompt, args.streaming, args.systemPrompt);
    } finally {
      isRetryingRef.current = false;
    }
  };

  return {
    loading,
    loadingMessage,
    debugLogs,
    aiConnectionStatus: provider.aiConnectionStatus,
    isTestingConnection: provider.isTestingConnection,
    availableModels: provider.availableModels,
    geminiModels: provider.geminiModels,
    geminiCategories: provider.geminiCategories,
    geminiCatalogSource: provider.geminiCatalogSource,
    modelNotice: provider.modelNotice,
    selectedModel: provider.selectedModel,
    selectedProvider: provider.selectedProvider,
    fallbackModel: provider.fallbackModel,
    loadingModels: provider.loadingModels,
    enableThinking,
    thinkingBudget,
    thinkingProcess,
    interpretation,
    showRawResponse,
    savedRawText,
    recoveredPartialText,
    lastGenerationStatus,
    firstChunkReceived,
    answeredModel,
    generationNotice,
    setDebugLogs,
    setSelectedModel: provider.setSelectedModel,
    setSelectedProvider: provider.setSelectedProvider,
    setFallbackModel: provider.setFallbackModel,
    setEnableThinking,
    setThinkingBudget,
    setShowRawResponse,
    setInterpretation,
    // Edycja diagnozy jako całości (plan 4.2): zapis edytowanego markdownu
    // aktualizuje savedRawText, żeby edytor i widok „Pełna odpowiedź
    // Markdown" pokazywały zredagowaną treść.
    setSavedRawText,
    testAIConnection: provider.testAIConnection,
    loadFreeModels: provider.loadFreeModels,
    loadGeminiModels: provider.loadGeminiModels,
    generateInterpretation,
    retryGeneration,
    resetGenerationArtifacts
  };
};
